// src/Services/API.js - FINAL CORRECTED VERSION

import apiClient from "./commonRequest";

// --- User Login & Authentication ---
export const verifyPasswordApi = (data) => apiClient.post("/login/password", data);
export const requestOtpApi = (data) => apiClient.post("/login/otp-request", data);
export const verifyOtpApi = (data) => apiClient.post("/login/otp-verify", data);
export const refreshAccessTokenApi = () => apiClient.post("/login/refresh-access-token");
// Two-step sign-in (security release, Nov 2026). See Pages/Auth/Login.
export const mfaVerifyApi = (data) => apiClient.post("/login/mfa", data);
export const mfaSetupApi = (data) => apiClient.post("/login/mfa/setup", data);
export const mfaSetupConfirmApi = (data) => apiClient.post("/login/mfa/setup/confirm", data);
export const logoutApi = (token) =>
    apiClient.post("/login/logout", null, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
// Account locks and two-step verification, for administrators.
export const getAccountSecurityApi = () => apiClient.get("/admin/security/accounts");
export const unlockAccountApi = (email) => apiClient.post("/admin/security/unlock", { email });
export const resetMfaApi = (email) => apiClient.post("/admin/security/reset-mfa", { email });
export const forgotPassword = (data) => apiClient.post("/login/forgot-password", data);
export const resetPassword = (data) => apiClient.post("/login/reset-password", data);
export const activateUser = (data) => apiClient.post("/login/activate-user", data);
export const getMe = () => apiClient.get("/me");

export const getYfConfig = () => apiClient.get("/yf/config");
export const getBoldBIEmbedConfig = (caseId) => apiClient.get(`/boldbi/embed-config?caseId=${caseId}`);

// Distinct (Account_Number, Statement_Year) pairs for a case. Feeds the React
// account/year filter bar in BoldBIDashboard, which drives the dashboard via
// viewer.updateFilters (param_account_t1 / param_year) instead of Bold BI combo
// widgets. Scoped server-side by case, so it returns only this case's accounts.
export const getCaseAccountsYears = (caseId) => apiClient.get(`/case/${caseId}/accounts-years`);

// Count of inter-account transfers for a case, scoped by the Fund Flow filter
// selection (source / dest / transfer year). Feeds the Fund Flow empty-state
// overlay in BoldBIDashboard: when this returns { count: 0 }, the overlay shows
// "No inter-account transactions for this case".
//
// NOTE: the backend endpoint is not built yet. Until it exists this request
// 404s, the caller (Step 4e effect in BoldBIDashboard) swallows the error, and
// the overlay stays hidden — the dashboard behaves exactly as before. When the
// endpoint is added, the message lights up with no further frontend change.
//
// caseId here is the numeric case sequence (same as every other /case/{caseId}
// endpoint). The v_interaccount_transfers view filters on the case LABEL, so the
// backend must resolve label-from-sequence before the count query — same pattern
// the other /case/{caseId} endpoints already use internally.
// params: { source, dest, year } — comma-separated lists or 'ALL'.
export const getInterAccountTransferCount = (caseId, params) =>
    apiClient.get(`/case/${caseId}/interaccount-count`, { params });

// --- PDF & File Management ---
export const UploadPdfApi = (payload) => {
  const { formData, queryParams } = payload;
  let url = '/pdf';
  if (queryParams && queryParams.length > 0) {
    url += `?${queryParams.join('&')}`;
  }
  return apiClient.post(url, formData, {
    headers: {
      'Content-Type': undefined
    }
  });
};

export const downloadPdfApi = (sequence) => apiClient.get(`/pdf/${sequence}`);

// We removed { responseType: 'blob' } so Axios parses the JSON automatically.
export const downloadPdfXlsxApi = (sequence) => apiClient.get(`/pdf/${sequence}/xlsx`);

export const downloadOriginalPdfApi = (sequence) => apiClient.get(`/pdf/${sequence}/original-pdf`, {
  responseType: 'blob'
});

export const deleteFile = (sequence) => apiClient.delete(`/pdf/${sequence}`);
export const getFilesData = (data = {}) => {
  const params = {
    caseId: data.caseNumber,
    documentType: data.documentType,
    startDate: data.startDate,
    endDate: data.endDate,
    status: data.status,
  };
  return apiClient.get("/pdf", { params });
};
export const getPdfByAccount = (accountId) => apiClient.get(`/pdf/account/${accountId}`);
export const getPdfByStatement = (statementId) => apiClient.get(`/pdf/statement/${statementId}`);
export const getPdfByTransaction = (transactionId) => apiClient.get(`/pdf/transaction/${transactionId}`);

// --- User Management ---
export const getUsers = () => apiClient.get("/user");
export const getUsersOfCustomer = (data = {}) => apiClient.get("/user/customer", { params: { activatedOnly: data.activatedOnly } });
export const createUser = (data) => apiClient.post("/user", data);
export const getUserById = (userId) => apiClient.get(`/user/${userId}`);
export const updateUser = (data) => apiClient.put(`/user/${data?.UserID}`, data);
export const deleteUserById = (userId) => apiClient.delete(`/user/${userId}`);

// --- Admin Management ---
export const convertUserToAdmin = (userSequence) => apiClient.post(`/user/${userSequence}/convert-to-admin`);
// Turn a user off (they cannot sign in) or back on. Case assignments are kept.
export const setUserActive = (userSequence, active) => apiClient.put(`/user/${userSequence}/active`, { active });
// Primary Administrator hands the role to another administrator.
export const makePrimaryAdmin = (adminSequence) => apiClient.post(`/admin/${adminSequence}/make-primary`);
// Primary Administrator: move a user to another administrator.
export const reassignUserAdmin = (userSequence, adminId) => apiClient.put(`/user/${userSequence}/admin`, { adminId });
// Primary Administrator: remove an administrator (they become a User under the primary).
export const removeAdmin = (adminSequence) => apiClient.post(`/admin/${adminSequence}/remove`);
export const getAdmins = () => apiClient.get("/admin");
export const getAdminById = (adminSequence) => apiClient.get(`/admin/${adminSequence}`);
export const updateAdmin = (data) => apiClient.put(`/admin/${data?.UserID}`, data);

// --- Case Management ---
export const getCases = (data = {}) => apiClient.get("/case", { params: { startDate: data.startDate, endDate: data.endDate } });
export const createCase = (data) => apiClient.post("/case", data);
export const getCaseById = (caseSequence) => apiClient.get(`/case/${caseSequence}`);
export const getCaseStatementWindow = (caseSeq) => apiClient.get(`/categorization/${caseSeq}/window`);
export const updateCase = (data) => apiClient.put(`/case/${data?.sequence}`, data);
export const deleteCaseById = (caseId) => apiClient.delete(`/case/${caseId}`);
export const getCaseAssignedUsers = (caseId) => apiClient.get(`/case/assign/${caseId}`);
export const assignUsers = (data) => apiClient.put(`/case/assign/${data.caseId}`, data.users);
export const getCasesData = () => apiClient.get("/case");

// --- Customer/Account Management ---
export const getAccounts = (data) => apiClient.get("/customer");
export const createAccount = (data) => apiClient.post("/customer", data);
export const getCustomerById = (customerSequence) => apiClient.get(`/customer/${customerSequence}`);
export const updateCustomer = (data) => apiClient.put(`/customer/${data?.customerSequence}`, data);
export const deleteAccountById = (customerId) => apiClient.delete(`/customer/${customerId}`);
export const getAllCompaniesJson = () => apiClient.get("/customer");

// --- Data Extraction & InferIQ ---
export const sampleInferIQApi = (data) => apiClient.post("/inferIQJson?page=1&pageSize=5", data);
export const getExtractDataApi = (data) => apiClient.get("/extraction", { params: { pageSize: data?.pageSize, page: data?.page } });
export const upodateJsonFileData = (data) => apiClient.post("/inferIQUpdate", data);

// --- Bank Account, Statement, Transaction (from PDF) ---
export const getAccount = (accountId) => apiClient.get(`/account/${accountId}`);
export const editAccount = (accountId, data) => apiClient.put(`/account/${accountId}`, data);
export const getStatement = (statementId) => apiClient.get(`/statement/${statementId}`);
export const editStatement = (statementId, data) => apiClient.put(`/statement/${statementId}`, data);
export const getTransactionsOnSamePage = (transactionId) => apiClient.get(`/transaction/${transactionId}/page`);
export const editTransaction = (transactionId, data) => apiClient.put(`/transaction/${transactionId}`, data);
export const createTransaction = (statementSeq, pdfDocumentSeq, pageNumber, data, anchorSeq, placement) => apiClient.post(`/transaction/statement/${statementSeq}/pdf/${pdfDocumentSeq}/page/${pageNumber}`, data, { params: anchorSeq ? { anchorSeq, placement } : {} });
export const deleteTransaction = (transactionId) => apiClient.delete(`/transaction/${transactionId}`);
// ADDED 2026-10-03: change every instance of a counterparty in the case.
// getSamePayee returns { payee, matchCount, accountCount, checkImageCount }.
export const getSamePayee = (transactionId) => apiClient.get(`/transaction/${transactionId}/same-payee`);
// data: { payee: string | null, expectedCount: number }
export const editPayeeForAll = (transactionId, data) => apiClient.put(`/transaction/${transactionId}/payee-all`, data);
// ADDED 2026-10-01: deposit slips for a case (supporting documents; read only).
export const getDepositSlips = (caseSeq) => apiClient.get(`/deposit-slip/case/${caseSeq}`);
// ADDED 2026-10-02: deposit slip correction (audited; never changes the statement).
export const getDepositSlip = (id) => apiClient.get(`/deposit-slip/${id}`);
export const getDepositSlipStatements = (id) => apiClient.get(`/deposit-slip/${id}/statements`);
export const updateDepositSlip = (id, data) => apiClient.put(`/deposit-slip/${id}`, data);

// ADDED 2026-10-03: account merge. The investigator picks the account to keep and
// the one to merge into it; the duplicate's statements move to the survivor and the
// duplicate is removed, recorded as one event in the correction audit trail.
export const getMergeAccounts = (caseSeq) => apiClient.get(`/account-merge/${caseSeq}/accounts`);
export const previewAccountMerge = (caseSeq, survivor, duplicate) =>
    apiClient.get(`/account-merge/${caseSeq}/preview`, { params: { survivor, duplicate } });
export const mergeAccounts = (caseSeq, survivorSequence, duplicateSequence) =>
    apiClient.post(`/account-merge/${caseSeq}`, { survivorSequence, duplicateSequence });

// Sequence lookups — called from BoldBIDashboard when an investigator clicks a
// grid row to open the correction panel. Bold BI masks hidden column values so
// we resolve the sequence ID from visible field values via these endpoints.
export const findTransactionSequence = (caseId, accountNumber, transactionDate, debit, credit) =>
    apiClient.get("/sequence-lookup/transaction", { params: { caseId, accountNumber, transactionDate, debit, credit } });

export const findStatementSequence = (caseId, accountNumber, statementYear) =>
    apiClient.get("/sequence-lookup/statement", { params: { caseId, accountNumber, statementYear } });

export const findAccountSequence = (caseId, accountNumber) =>
    apiClient.get("/sequence-lookup/account", { params: { caseId, accountNumber } });

// --- Settings ---
export const getSettings = () => apiClient.get("/settings");
export const updateSetting = (data) => apiClient.post("/settings", data);

// --- Yellowfin (BI) ---
export const generateYfLoginToken = () => apiClient.post("/yf/login-token", {});
export const deleteYellowfinLoginToken = (yfLoginTokenId) => apiClient.delete(`/yf/login-token/${yfLoginTokenId}`);

// --- Registration & Subscription (Maxio/Chargify) ---
export const getBillingToken = () => apiClient.get("/billing/token");
export const registerPrimaryAdmin = (data) => apiClient.post("/register/primary-admin", data);
export const getChargifyjsSecurityToken = () => apiClient.get("/maxio/security-token");
export const createMaxioSubscriptionWithPaymentToken = (data) => apiClient.post("/maxio/subscription/payment-token", data);
export const createMaxioSubscriptionWithPaymentAuthCode = (data) => apiClient.post("/maxio/subscription/payment-auth-code", data);
// Super admin only: issue a payment authorization code ({ email }) and list recent codes.
export const generatePaymentAuthCode = (data) => apiClient.post("/payment-auth-code/generate", data);
export const listPaymentAuthCodes = () => apiClient.get("/payment-auth-code");
// New paid signup paying by bank (eCheck). No body — the backend reads the plan from
// the authenticated customer record. Creates a REMITTANCE subscription: Maxio issues
// an invoice with bank/ACH instructions, the full allocation is created immediately,
// and consumption is capped at the 500-page bridge until payment is recorded.
// Rejects pay_as_you_go (billed daily in arrears) and free_trial.
export const createSubscriptionWithRemittance = () => apiClient.post("/maxio/subscription/remittance");
// Free-trial: no body — the authenticated context identifies the primary admin/customer.
// Backend allocates 50 pages at the $0 trial plan and forces subscriptionStatus = "active".
export const createFreeTrialSubscription = () => apiClient.post("/maxio/subscription/free-trial");
// Trial -> paid conversion: { token, planName, companyName, companyStreetAddress,
// companyStreetAddress2, companyCity, companyState, companyPostalCode, companyCountryCode }.
// Backend creates the paid sub first, then winds down the $0 trial sub.
export const convertTrialToPaid = (data) => apiClient.post("/maxio/subscription/convert-trial", data);

// Prepaid bundle purchase (self-serve, subscribed users): { bundlePlanName, paymentMethod, token? }.
// paymentMethod is "card" (token required), "ach", or "invoice" (no token — both create a
// REMITTANCE invoice). Backend routes by current plan: new/PAYG -> new bundle sub; existing
// bundle -> allocate to same sub (top-up, bills method on file).
export const purchaseBundle = (data) => apiClient.post("/maxio/subscription/bundle", data);

export const getMaxioBillingPortalLink = () => apiClient.get("/maxio/billing-portal-link");
export const getCurrentSubscriptionInfo = () => apiClient.get("/maxio/current-subscription-info");
export const schedulePlanChange = (planName) => apiClient.post(`/maxio/schedule-plan-change/${planName}`);
export const getScheduledPendingPlanChanges = () => apiClient.get("/scheduled-plan-changes/pending");
export const cancelPendingPlanChanges = (data) => apiClient.delete("/scheduled-plan-changes/pending");

// =====================================================================
// --- Categorization Workbench ---
// Backend: CategorizationController @RequestMapping("/categorization"),
// case-scoped under /categorization/{caseSeq}/... — matching your /case/{id} style.
// caseSeq is the numeric Case_Sequence (row.sequence), same id used everywhere else.
// =====================================================================

// Canonical category list for the pickers (id, key, name, type).
export const getCategories = () => apiClient.get("/categorization/categories");

export const getCategoryTree = () => apiClient.get("/categorization/category-tree");

// Deduplicated, uncategorized payees for the case (the By Payee grid).
// params: { search, sort, page, size } — sort is count_desc | total_debit_desc | total_credit_desc.
export const getCategorizationGroups = (caseSeq, params) =>
    apiClient.get(`/categorization/${caseSeq}/groups`, { params });

// Assign every (eligible) transaction under one payee group to a category.
// body: { groupKey, categoryId, source, overwriteExisting }
export const assignCategoryGroup = (caseSeq, data) =>
    apiClient.post(`/categorization/${caseSeq}/assign-group`, data);

// Assign an explicit list of transactions (By Transaction / overrides).
// body: { transactionSeqs, categoryId, source, overwriteExisting }
export const assignCategoryTransactions = (caseSeq, data) =>
    apiClient.post(`/categorization/${caseSeq}/assign-transactions`, data);

// Create a rule. body: { scope, caseSequence, matchType, matchValue, amountMin, amountMax, categoryId, priority }
export const createCategoryRule = (caseSeq, data) =>
    apiClient.post(`/categorization/${caseSeq}/rules`, data);

// Preview a draft rule without saving. body: same as createCategoryRule.
// params: { overwriteExisting }
export const previewCategoryRule = (caseSeq, data, overwriteExisting = false) =>
    apiClient.post(`/categorization/${caseSeq}/rules/preview`, data, { params: { overwriteExisting } });

// Run rules. body: { ruleIds (null = all active for case), overwriteExisting }
export const runCategoryRules = (caseSeq, data) =>
    apiClient.post(`/categorization/${caseSeq}/rules/run`, data);

// Completeness counts: { totalTxns, assigned, excluded, uncategorized }.
export const getCategorizationCompleteness = (caseSeq) =>
    apiClient.get(`/categorization/${caseSeq}/completeness`);

// Form roll-up preview. params: { formTemplateId, windowStart, windowEnd }.
export const getCategorizationRollup = (caseSeq, params) =>
    apiClient.get(`/categorization/${caseSeq}/rollup`, { params });

export const exportCategorizationReport = (caseSeq, params) =>
  apiClient.get(`/categorization/${caseSeq}/report/export`, {
    params,                 // { formTemplateId, windowStart, windowEnd, caseLabel }
    responseType: "blob",   // PDF bytes, not JSON
  });

// Filtered, paged transaction list for the By Transaction tab.
// params: { status: 'uncategorized'|'assigned'|'all', nullPayeeOnly: bool, search, page, size }
export const getCategorizationTransactions = (caseSeq, params) =>
    apiClient.get(`/categorization/${caseSeq}/transactions`, { params });

// Case Excel export (Primary Admin). Returns { url } — a presigned download link —
// like downloadPdfXlsxApi (NOT blob), so the page anchor-clicks res.data.url.
export const getCaseExportUrl = (caseSeq) =>
    apiClient.get(`/admin/case/${caseSeq}/export`);

// Case delete (Primary Admin). DELETE /admin/case/{caseSeq} — gated server-side
// by assertPrimaryAdminForCase, same as the export/audit calls above.
export const deleteCase = (caseSeq, password) =>
    apiClient.delete(`/admin/case/${caseSeq}`, { data: { password } });

export const getCaseAuditTrail = (caseSeq) => apiClient.get(`/admin/case/${caseSeq}/audit`);

export const exportCaseAuditPdf = (caseSeq) =>
    apiClient.get(`/admin/case/${caseSeq}/audit/export`, { responseType: 'blob' });

// =====================================================================
// --- Payee Merge Review ---
// Backend: PayeeMergeController @RequestMapping("/payee-merge"),
// case-scoped under /payee-merge/{caseSeq}/... — matching the /categorization style.
// caseSeq is the numeric Case_Sequence, same id used everywhere else.
//
// Similarity scoring SUGGESTS pairs; it never merges. Every approve/rename/reject/
// revoke is attributed to the caller from the security context and written to an
// append-only audit table, so a merged payee can be explained and undone.
//
// UPDATED 7/29/2026 — the model moved from pairwise redirection to a NAMED GROUP.
// A group holds any number of spellings and is reported under one name, which may
// be a name that appears on no statement ("Center for Allied Health" where every
// statement says "CENTER FOR ALLIED HEAL"). Two calls added, /groups and /rename;
// nothing was removed, so anything already calling /merges keeps working.
// =====================================================================

// Suggested merges above the similarity threshold, excluding pairs already
// approved or rejected. Read-only — calling this merges nothing.
// params: { threshold } — optional 0..1 override; server default is 0.70.
//
// Each candidate now also carries: suggestedLabel and suggestionBasis (TRUNCATION
// | SPACING | VOLUME, why the server leaned that way), similarityIgnoringSpacing,
// a preview of the combined totals, and any names already grouped with either side.
export const getPayeeMergeCandidates = (caseSeq, threshold) =>
    apiClient.get(`/payee-merge/${caseSeq}/candidates`, {
        params: threshold ? { threshold } : {},
    });

// Every canonical payee in the case with its evidence (counts, amounts, date
// range, and the raw unnormalised spellings that rolled up into it).
export const getPayeeList = (caseSeq) =>
    apiClient.get(`/payee-merge/${caseSeq}/payees`);

// Merges currently in force, ONE ROW PER MEMBER.
// Superseded by getPayeeMergeGroups for the review screen — a flat member list no
// longer says which names belong together now that a group can hold more than two.
// Kept because other callers may still use it.
export const getPayeeMerges = (caseSeq) =>
    apiClient.get(`/payee-merge/${caseSeq}/merges`);

// The same merges folded into groups: each with its reported name, whether that
// name was supplied by the investigator rather than taken from a statement, its
// members, and the combined transaction count and dollar total.
export const getPayeeMergeGroups = (caseSeq) =>
    apiClient.get(`/payee-merge/${caseSeq}/groups`);

// Full decision history: approvals, renames, rejections and withdrawals.
// Append-only. Includes what the server itself suggested and whether the reviewer
// kept a different name — recomputed server-side, never sent from here.
export const getPayeeMergeAudit = (caseSeq) =>
    apiClient.get(`/payee-merge/${caseSeq}/audit`);

// Same entity — both names are reported under one name.
// body: { variantCanonical, canonicalName, displayName, similarity, reason }
//
// canonicalName is the surviving spelling the reviewer chose. displayName is
// optional: send it when the correct name appears on no statement, and leave it
// null to report under canonicalName. Deliberately NOT sent: any claim about what
// the software suggested — the server recomputes that itself, because a
// client-supplied "this is what you proposed" is not evidence of anything.
export const approvePayeeMerge = (caseSeq, data) =>
    apiClient.post(`/payee-merge/${caseSeq}/approve`, data);

// Change the name a group is reported under. Membership is untouched and the
// statements are never altered — only the label the report prints.
// body: { canonicalName (the current name), displayName (the new one), reason }
export const renamePayeeGroup = (caseSeq, data) =>
    apiClient.post(`/payee-merge/${caseSeq}/rename`, data);

// Manual merge: names the reviewer picked herself, not a suggested pair.
// body: { names: [..at least two..], canonicalName (one of names), displayName, reason }
// All or nothing on the server: if any name fails, none are merged.
export const mergePayeeNames = (caseSeq, data) =>
    apiClient.post(`/payee-merge/${caseSeq}/merge-names`, data);

// Different entities. Recorded so the pair stops being suggested.
// body: { variantCanonical, canonicalName, similarity, reason }
export const rejectPayeeMerge = (caseSeq, data) =>
    apiClient.post(`/payee-merge/${caseSeq}/reject`, data);

// Withdraw one name from its group. The alias row goes; the audit history remains.
// If it leaves a single name behind, the group dissolves — a group of one would
// put a payee in the report under a name it was never merged into.
// body: { variantCanonical, reason }
export const revokePayeeMerge = (caseSeq, data) =>
    apiClient.post(`/payee-merge/${caseSeq}/revoke`, data);

// =====================================================================
// --- Counterparty Review (crypto first) ---
// Backend: CounterpartyReviewController @RequestMapping("/counterparty-review"),
// case-scoped under /counterparty-review/{caseSeq}/... like /payee-merge.
// A flag is a lead for review: the transaction's payee or description matched an
// entry on the global counterparty list. Nothing is confirmed until an
// investigator decides, and descriptions, payees and amounts are never changed.
// =====================================================================

// Flagged counterparties for the case, with status and totals, plus a summary.
export const getCounterpartyReview = (caseSeq) =>
    apiClient.get(`/counterparty-review/${caseSeq}`);

// The transactions behind one flagged counterparty.
export const getCounterpartyTransactions = (caseSeq, reviewKey, typeKey) =>
    apiClient.get(`/counterparty-review/${caseSeq}/transactions`, { params: { reviewKey, typeKey } });

// Confirm or dismiss. body: { reviewKey, typeKey, decision: 'CONFIRMED'|'DISMISSED', note }
export const decideCounterparty = (caseSeq, body) =>
    apiClient.post(`/counterparty-review/${caseSeq}/decide`, body);

// Return a decision to Possible - review. body: { reviewKey, typeKey, note }
export const reopenCounterparty = (caseSeq, body) =>
    apiClient.post(`/counterparty-review/${caseSeq}/reopen`, body);

// Every confirm, dismiss and reopen for the case. Append-only.
export const getCounterpartyHistory = (caseSeq) =>
    apiClient.get(`/counterparty-review/${caseSeq}/history`);

// Payment patterns around flagged counterparties (ADDED 2026-11-02): first payment,
// crypto ATM after cash, payment after a deposit. Dismissed counterparties left out.
export const getCounterpartyRedFlags = (caseSeq) =>
    apiClient.get(`/counterparty-review/${caseSeq}/red-flags`);

// Re-match the case against the current list (for cases processed before flags existed).
export const rebuildCounterpartyFlags = (caseSeq) =>
    apiClient.post(`/counterparty-review/${caseSeq}/rebuild`);

// --- Counterparty Types list (eFraud Services super administrator only) ---
export const getCounterpartyTypes = () => apiClient.get('/admin/counterparty-types');
export const createCounterpartyType = (body) => apiClient.post('/admin/counterparty-types', body);
export const updateCounterpartyType = (typeId, body) => apiClient.put(`/admin/counterparty-types/${typeId}`, body);
export const deactivateCounterpartyType = (typeId) => apiClient.post(`/admin/counterparty-types/${typeId}/deactivate`);
export const reactivateCounterpartyType = (typeId) => apiClient.post(`/admin/counterparty-types/${typeId}/reactivate`);
export const createCounterpartyPattern = (body) => apiClient.post('/admin/counterparty-types/patterns', body);
export const updateCounterpartyPattern = (patternId, body) => apiClient.put(`/admin/counterparty-types/patterns/${patternId}`, body);
export const deactivateCounterpartyPattern = (patternId) => apiClient.post(`/admin/counterparty-types/patterns/${patternId}/deactivate`);
export const reactivateCounterpartyPattern = (patternId) => apiClient.post(`/admin/counterparty-types/patterns/${patternId}/reactivate`);
// Re-match every case against the current list. Returns at once; the work runs in the background.
export const rebuildAllCounterpartyFlags = () => apiClient.post('/admin/counterparty-types/rebuild-all');
